/**
 * Deliveries Page
 *
 * View and replay webhook delivery attempts.
 * subscriptionId is REQUIRED for listing.
 */

import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { apiClient } from '../lib/api';

import { RequestPanel } from '../components/RequestPanel';
import { Code } from '../components/JsonViewer';
import type { ApiResult, DeliveryAttempt, DeliveryListResponse, ReplayDeliveryResponse } from '../lib/types';

export function Deliveries() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state
  const [subscriptionId, setSubscriptionId] = useState(searchParams.get('subscriptionId') ?? '');
  const [deliveries, setDeliveries] = useState<DeliveryAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<DeliveryListResponse> | null>(null);

  // Detail view
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryAttempt | null>(null);

  // Replay state
  const [replayingId, setReplayingId] = useState<string | null>(null);
  const [replayResult, setReplayResult] = useState<ApiResult<ReplayDeliveryResponse> | null>(null);

  const loadDeliveries = async () => {
    if (!subscriptionId.trim()) return;
    setLoading(true);
    setResult(null);

    const res = await apiClient.listDeliveries({
      subscriptionId: subscriptionId.trim(),
      limit: 100,
    });

    setResult(res);
    if (res.response.data) {
      setDeliveries(res.response.data.items);
    }
    setLoading(false);

    // Update URL
    setSearchParams({ subscriptionId: subscriptionId.trim() });
  };

  // Auto-load if subscriptionId is in URL
  useEffect(() => {
    const subId = searchParams.get('subscriptionId');
    if (subId) {
      setSubscriptionId(subId);
      apiClient.listDeliveries({ subscriptionId: subId, limit: 100 }).then((res) => {
        setResult(res);
        if (res.response.data) {
          setDeliveries(res.response.data.items);
        }
      });
    }
  }, [searchParams]);

  const handleReplay = async (delivery: DeliveryAttempt) => {
    setReplayingId(delivery.id);
    setReplayResult(null);

    const res = await apiClient.replayDelivery({
      outboxEventId: delivery.outboxEventId,
      subscriptionId: delivery.subscriptionId,
    });

    setReplayResult(res);
    setReplayingId(null);

    // Refresh list on success
    if (res.response.status === 202) {
      await loadDeliveries();
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'delivered':
        return 'var(--emerald-600)';
      case 'pending':
        return 'var(--amber-600)';
      case 'failed':
        return 'var(--red-600)';
      case 'dead_letter':
        return 'var(--red-700)';
      default:
        return 'var(--slate-500)';
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
          Deliveries
        </h1>
        <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
          View and replay webhook delivery attempts
        </p>
      </div>

      {/* Filter */}
      <div className="card">
        <h2>Filter by Subscription</h2>
        <p className="text-muted text-small" style={{ marginBottom: 12 }}>
          subscriptionId is required to list deliveries.
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            className="input mono"
            style={{ flex: 1, maxWidth: 400 }}
            value={subscriptionId}
            onChange={(e) => setSubscriptionId(e.target.value)}
            placeholder="Enter Subscription ID (required)"
          />
          <button
            className="btn btn-primary"
            onClick={loadDeliveries}
            disabled={loading || !subscriptionId.trim()}
          >
            {loading ? 'Loading...' : 'Load Deliveries'}
          </button>
        </div>
        <div style={{ marginTop: 8 }}>
          <Link to="/console/subscriptions" className="text-small">
            View Subscriptions →
          </Link>
        </div>
      </div>

      {/* Results */}
      {deliveries.length > 0 && (
        <div className="card">
          <h2>
            Delivery Attempts
            <span className="text-muted text-small" style={{ marginLeft: 8 }}>
              ({deliveries.length} found)
            </span>
          </h2>

          <table className="table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Event ID</th>
                <th>Attempt #</th>
                <th>Status</th>
                <th>HTTP Status</th>
                <th>Attempted At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id}>
                  <td>
                    <Code>{d.id.slice(0, 12)}...</Code>
                  </td>
                  <td>
                    <Code>{d.outboxEventId.slice(0, 12)}...</Code>
                  </td>
                  <td>{d.attemptNumber}</td>
                  <td>
                    <span
                      className="status"
                      style={{
                        background:
                          d.status === 'delivered'
                            ? '#dcfce7'
                            : d.status === 'pending'
                              ? '#fef3c7'
                              : '#fee2e2',
                        color: getStatusColor(d.status),
                      }}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="mono">{d.httpStatus ?? '-'}</td>
                  <td className="text-muted text-small">
                    {new Date(d.attemptedAt).toLocaleString()}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: 12 }}
                        onClick={() => setSelectedDelivery(d)}
                      >
                        Details
                      </button>
                      {d.status === 'failed' && d.attemptNumber < 5 && (
                        <button
                          className="btn btn-primary"
                          style={{ padding: '4px 8px', fontSize: 12 }}
                          onClick={() => handleReplay(d)}
                          disabled={replayingId === d.id}
                        >
                          {replayingId === d.id ? '...' : 'Replay'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {subscriptionId && deliveries.length === 0 && !loading && result && (
        <div className="card">
          <p className="text-muted" style={{ textAlign: 'center' }}>
            No deliveries found for this subscription.
          </p>
        </div>
      )}

      <RequestPanel result={result} loading={loading} />
      {replayResult && (
        <div className="card">
          <h3>Replay Result</h3>
          {replayResult.response.status === 202 && (
            <div className="success-box" style={{ marginBottom: 12 }}>
              Replay scheduled successfully!
            </div>
          )}
          {replayResult.response.status === 409 && (
            <div className="error-box" style={{ marginBottom: 12 }}>
              <strong>Conflict:</strong> {replayResult.response.error?.message}
            </div>
          )}
          <RequestPanel result={replayResult} />
        </div>
      )}

      {/* Detail Modal */}
      {selectedDelivery && (
        <div className="card">
          <div className="flex justify-between items-center">
            <h2>Delivery Detail</h2>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 8px', fontSize: 12 }}
              onClick={() => setSelectedDelivery(null)}
            >
              Close
            </button>
          </div>

          <table className="table" style={{ maxWidth: 600 }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 500, width: 180 }}>ID</td>
                <td><Code>{selectedDelivery.id}</Code></td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Outbox Event ID</td>
                <td><Code>{selectedDelivery.outboxEventId}</Code></td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Subscription ID</td>
                <td><Code>{selectedDelivery.subscriptionId}</Code></td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Attempt Number</td>
                <td>{selectedDelivery.attemptNumber} / 5</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Status</td>
                <td>
                  <span style={{ color: getStatusColor(selectedDelivery.status), fontWeight: 600 }}>
                    {selectedDelivery.status.toUpperCase()}
                  </span>
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>HTTP Status</td>
                <td className="mono">{selectedDelivery.httpStatus ?? '(no response)'}</td>
              </tr>
              {selectedDelivery.errorMessage && (
                <tr>
                  <td style={{ fontWeight: 500 }}>Error</td>
                  <td className="text-small" style={{ color: '#dc2626' }}>
                    {selectedDelivery.errorMessage}
                  </td>
                </tr>
              )}
              {selectedDelivery.responseHash && (
                <tr>
                  <td style={{ fontWeight: 500 }}>Response Hash</td>
                  <td className="mono text-small">{selectedDelivery.responseHash}</td>
                </tr>
              )}
              <tr>
                <td style={{ fontWeight: 500 }}>Idempotency Key</td>
                <td className="mono text-small">{selectedDelivery.idempotencyKey}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Attempted At</td>
                <td>{new Date(selectedDelivery.attemptedAt).toLocaleString()}</td>
              </tr>
              {selectedDelivery.nextRetryAt && (
                <tr>
                  <td style={{ fontWeight: 500 }}>Next Retry At</td>
                  <td>{new Date(selectedDelivery.nextRetryAt).toLocaleString()}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Replay button in detail view */}
          {selectedDelivery.status === 'failed' && selectedDelivery.attemptNumber < 5 && (
            <div style={{ marginTop: 16 }}>
              <button
                className="btn btn-primary"
                onClick={() => handleReplay(selectedDelivery)}
                disabled={replayingId === selectedDelivery.id}
              >
                {replayingId === selectedDelivery.id ? 'Replaying...' : 'Replay This Delivery'}
              </button>
            </div>
          )}

          {selectedDelivery.status === 'dead_letter' && (
            <div
              className="error-box"
              style={{ marginTop: 16 }}
            >
              This delivery is dead-lettered after 5 attempts and cannot be replayed.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
